import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
	ActionSuggestion,
	Asset,
	Attack,
	Claim,
	DetectPhaseState,
	EvaluatedClaim,
	Phase,
	RespondPhaseState,
	RiskAnalysis,
	SentinelState,
	TimelineEvent,
	Verification,
	VerifyPhaseState,
	VideoMetadata
} from 'src/types/sentinel';

// --- HELPERS ---
const completePendingSteps = (state: SentinelState, phase: Phase) => {
	for (const key of Object.keys(state.timeline)) {
		const event = state.timeline[key];
		if (event.phase === phase && (event.stepStatus === 'pending' || event.stepStatus === 'in_progress')) {
			event.stepStatus = 'completed';
		}
	}
};

// --- INITIAL STATES ---

const initialDetectState: DetectPhaseState = {
	status: 'idle',
	startedAt: null,
	completedAt: null,
	videoDescription: null,
	allClaims: [],
	attacks: [],
	selectedClaims: [],
	assets: [],
	keyframes: []
};

const initialVerifyState: VerifyPhaseState = {
	status: 'idle',
	startedAt: null,
	completedAt: null,
	verifications: {}
};

const initialRespondState: RespondPhaseState = {
	status: 'idle',
	startedAt: null,
	completedAt: null,
	riskAnalysis: null,
	actionSuggestions: []
};

const initialState: SentinelState = {
	videoUrl: null,
	refreshCache: false,
	processId: null,
	status: 'idle',
	currentPhase: null,
	metadata: null,
	isVideoPlayerVisible: false,
	phases: {
		detect: { ...initialDetectState },
		verify: { ...initialVerifyState },
		respond: { ...initialRespondState }
	},
	timeline: {},
	progress: {
		percentage: 0,
		estimatedTimeRemaining: null
	},
	error: null
};

const sentinelSlice = createSlice({
	name: 'sentinel',
	initialState,
	reducers: {
		// ========== GENERAL ==========
		setVideoUrl: (state, action: PayloadAction<{ url: string; refreshCache?: boolean }>) => {
			state.videoUrl = action.payload.url;
			state.refreshCache = action.payload.refreshCache ?? false;
			state.processId = `process-${Date.now()}`;
			state.status = 'processing';
			state.currentPhase = null;
			state.metadata = null;
			state.phases.detect = { ...initialDetectState };
			state.phases.verify = { ...initialVerifyState };
			state.phases.respond = { ...initialRespondState };
			state.timeline = {};
			state.progress = { percentage: 0, estimatedTimeRemaining: null };
			state.error = null;
		},

		setVideoMetadata: (state, action: PayloadAction<VideoMetadata>) => {
			state.metadata = action.payload;
		},

		setCurrentPhase: (state, action: PayloadAction<Phase | null>) => {
			state.currentPhase = action.payload;
		},

		setVideoPlayerVisible: (state, action: PayloadAction<boolean>) => {
			state.isVideoPlayerVisible = action.payload;
		},

		updateProgress: (state, action: PayloadAction<{ percentage: number; estimatedTime?: number }>) => {
			state.progress.percentage = action.payload.percentage;
			if (action.payload.estimatedTime !== undefined) {
				state.progress.estimatedTimeRemaining = action.payload.estimatedTime;
			}
		},

		addOrUpdateTimelineEvent: (state, action: PayloadAction<Omit<TimelineEvent, 'id' | 'timestamp'>>) => {
			const { stepId } = action.payload;

			if (!stepId) {
				// biome-ignore lint/suspicious/noConsole: defensive reducer diagnostics until Sentry SDK is wired up
				console.warn('addOrUpdateTimelineEvent called without stepId');
				return;
			}

			const existingEvent = state.timeline[stepId];
			const timestamp = existingEvent ? existingEvent.timestamp : Date.now();

			state.timeline[stepId] = {
				...(existingEvent || {}),
				...action.payload,
				id: stepId,
				timestamp: timestamp
			};
		},

		addTimelineEvent: (state, action: PayloadAction<Omit<TimelineEvent, 'id' | 'timestamp'>>) => {
			const id = `event-${Date.now()}-${Math.random()}`;
			state.timeline[id] = {
				id,
				timestamp: Date.now(),
				...action.payload
			};
		},

		// ========== DETECT PHASE ==========
		detectPhaseStarted: (state) => {
			state.phases.detect.status = 'processing';
			state.phases.detect.startedAt = Date.now();
			state.currentPhase = 'detect';
		},

		detectPhaseDataReceived: (
			state,
			action: PayloadAction<{
				videoDescription: string;
				allClaims: Claim[];
				attacks: Attack[];
				assets?: Asset[];
			}>
		) => {
			state.phases.detect.videoDescription = action.payload.videoDescription;
			state.phases.detect.allClaims = action.payload.allClaims;
			state.phases.detect.attacks = action.payload.attacks;
			if (action.payload.assets) {
				state.phases.detect.assets = action.payload.assets;
			}
		},

		setDetectedKeyframes: (state, action: PayloadAction<string[]>) => {
			state.phases.detect.keyframes = action.payload;
		},

		detectPhaseFailed: (state, action: PayloadAction<string>) => {
			state.phases.detect.status = 'error';
			state.phases.detect.completedAt = Date.now();
			state.error = action.payload;
			state.status = 'error';
		},

		updateSelectedClaims: (state, action: PayloadAction<EvaluatedClaim[]>) => {
			const claimsWithIds = action.payload.map((claim, index) => ({
				...claim,
				id: index
			}));
			state.phases.detect.selectedClaims = claimsWithIds;
			state.phases.detect.status = 'completed';
			state.phases.detect.completedAt = Date.now();
			completePendingSteps(state, 'detect');

			state.phases.verify.verifications = {};
			for (const claim of claimsWithIds) {
				state.phases.verify.verifications[claim.id] = {
					id: claim.id,
					claim: claim.claim,
					status: 'pending'
				} as Verification;
			}
		},

		// ========== VERIFY PHASE ==========
		verifyPhaseStarted: (state) => {
			state.phases.verify.status = 'processing';
			state.phases.verify.startedAt = Date.now();
			state.currentPhase = 'verify';
		},

		updateClaimVerification: (state, action: PayloadAction<{ id: number; data: Partial<Verification> }>) => {
			const { id, data } = action.payload;
			const { verifications } = state.phases.verify;
			if (!verifications[id]) {
				verifications[id] = { id, claim: '' } as Verification;
			}
			state.phases.verify.verifications[id] = {
				...verifications[id],
				...data,
				id
			};
		},

		verifyPhaseCompleted: (state) => {
			state.phases.verify.status = 'completed';
			state.phases.verify.completedAt = Date.now();
			completePendingSteps(state, 'verify');
		},

		verifyPhaseFailed: (state, action: PayloadAction<string>) => {
			state.phases.verify.status = 'error';
			state.phases.verify.completedAt = Date.now();
			state.error = action.payload;
			state.status = 'error';
		},

		// ========== RESPOND PHASE ==========
		respondPhaseStarted: (state) => {
			state.phases.respond.status = 'processing';
			state.phases.respond.startedAt = Date.now();
			state.currentPhase = 'respond';
		},

		respondPhaseRiskAnalysisReceived: (state, action: PayloadAction<RiskAnalysis>) => {
			state.phases.respond.riskAnalysis = action.payload;
		},

		respondPhaseActionsReceived: (state, action: PayloadAction<ActionSuggestion[]>) => {
			state.phases.respond.actionSuggestions = action.payload;
			state.phases.respond.status = 'completed';
			state.phases.respond.completedAt = Date.now();
			completePendingSteps(state, 'respond');
		},

		respondPhaseFailed: (state, action: PayloadAction<string>) => {
			state.phases.respond.status = 'error';
			state.phases.respond.completedAt = Date.now();
			state.error = action.payload;
			state.status = 'error';
		},

		// ========== PROCESS COMPLETE ==========
		processCompleted: (state) => {
			state.status = 'completed';
			state.currentPhase = null;
			state.progress.percentage = 100;
			state.progress.estimatedTimeRemaining = 0;
		},

		resetProcess: () => {
			return { ...initialState };
		}
	}
});

export const {
	setVideoUrl,
	setVideoMetadata,
	setCurrentPhase,
	setVideoPlayerVisible,
	updateProgress,
	addTimelineEvent,
	addOrUpdateTimelineEvent,

	detectPhaseStarted,
	detectPhaseDataReceived,
	setDetectedKeyframes,
	detectPhaseFailed,
	updateSelectedClaims,

	verifyPhaseStarted,
	updateClaimVerification,
	verifyPhaseCompleted,
	verifyPhaseFailed,

	respondPhaseStarted,
	respondPhaseRiskAnalysisReceived,
	respondPhaseActionsReceived,
	respondPhaseFailed,

	processCompleted,
	resetProcess
} = sentinelSlice.actions;

export default sentinelSlice.reducer;
